import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from "react";

import { getThemeModeVariants, type ThemeModeVariant } from "@/theme/ui-branding";

/** @deprecated Use ThemeModeVariant from ui-branding; kept for existing imports. */
export type Theme = ThemeModeVariant;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemeModeVariant;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: ThemeModeVariant;
  setTheme: (theme: ThemeModeVariant) => void;
  themeModeVariants: ThemeModeVariant[];
  isThemeSelectionEnabled: boolean;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  themeModeVariants: ["light", "dark", "system"],
  isThemeSelectionEnabled: true,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

// Theme lives in localStorage; setTheme notifies subscribers. SSR and hydration render the
// fallback (getServerSnapshot) so the server HTML matches, then the stored value takes over.
const themeListeners = new Set<() => void>();
function subscribeTheme(listener: () => void) {
  themeListeners.add(listener);
  return () => {
    themeListeners.delete(listener);
  };
}

function fallbackTheme(defaultTheme: ThemeModeVariant, variants: ThemeModeVariant[]): ThemeModeVariant {
  return variants.includes(defaultTheme) ? defaultTheme : variants[0];
}

function pickInitialTheme(
  storageKey: string,
  defaultTheme: ThemeModeVariant,
  variants: ThemeModeVariant[]
): ThemeModeVariant {
  if (variants.length === 1) {
    const only = variants[0];
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, only);
    }
    return only;
  }
  const stored = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
  if (stored === "light" || stored === "dark" || stored === "system") {
    if (variants.includes(stored)) {
      return stored;
    }
  }
  return fallbackTheme(defaultTheme, variants);
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  ...props
}: ThemeProviderProps) {
  const themeModeVariants = useMemo(() => getThemeModeVariants(), []);
  const isThemeSelectionEnabled = themeModeVariants.length > 1;

  const theme = useSyncExternalStore(
    subscribeTheme,
    () => pickInitialTheme(storageKey, defaultTheme, themeModeVariants),
    () => fallbackTheme(defaultTheme, themeModeVariants)
  );

  const resolvedTheme = themeModeVariants.length === 1 ? themeModeVariants[0] : theme;

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    if (resolvedTheme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (resolvedTheme !== "system") {
      return;
    }
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(mq.matches ? "dark" : "light");
    };
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [resolvedTheme]);

  const setTheme = (next: ThemeModeVariant) => {
    if (!isThemeSelectionEnabled) {
      return;
    }
    if (!themeModeVariants.includes(next)) {
      return;
    }
    localStorage.setItem(storageKey, next);
    themeListeners.forEach(listener => listener());
  };

  const value = {
    theme: resolvedTheme,
    setTheme,
    themeModeVariants,
    isThemeSelectionEnabled,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
